import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  List,
  ListItem,
  ListIcon,
  Code,
  Box,
  IconButton,
  useToast,
  Flex,
} from '@chakra-ui/react';
import { MdWarning, MdContentCopy } from 'react-icons/md';

interface ErrorMessagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  errorMessages: (string | object)[];
  title?: string;
}

const ErrorMessagesModal: React.FC<ErrorMessagesModalProps> = ({
  isOpen,
  onClose,
  errorMessages,
  title = 'Error Messages',
}) => {
  const toast = useToast();

  const handleCopyToClipboard = async (msg: object) => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(msg, null, 2));
      toast({
        title: 'Copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Failed to copy',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    }
  };

  const renderErrorMessage = (msg: string | object, idx: number) => {
    if (typeof msg === 'string') {
      return (
        <ListItem key={idx} display="flex" alignItems="start">
          <ListIcon as={MdWarning} color="red.500" mt={1} />
          <Text fontSize="sm">{msg}</Text>
        </ListItem>
      );
    }

    return (
      <ListItem key={idx} display="flex" alignItems="start">
        <ListIcon as={MdWarning} color="red.500" mt={1} />
        <Box flex="1">
          <Flex justifyContent="space-between" alignItems="start" mb={1}>
            <Text fontSize="sm" fontWeight="medium" color="gray.600">
              Error Object:
            </Text>
            <IconButton
              aria-label="Copy to clipboard"
              icon={<MdContentCopy />}
              size="xs"
              onClick={() => handleCopyToClipboard(msg)}
              variant="ghost"
            />
          </Flex>
          <Code
            display="block"
            whiteSpace="pre-wrap"
            p={2}
            borderRadius="md"
            fontSize="sm"
          >
            {JSON.stringify(msg, null, 2)}
          </Code>
        </Box>
      </ListItem>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          <VStack align="start" spacing={3}>
            <Text fontWeight="medium">
              The following errors occurred during cross-listing:
            </Text>
            <List spacing={2} w="100%">
              {errorMessages.map((msg, idx) => renderErrorMessage(msg, idx))}
            </List>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ErrorMessagesModal;
